import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from "@angular/common";
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, finalize, firstValueFrom, forkJoin, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { Group } from '../_models/group.model';
import { GroupCountryRegion } from '../_models/group-country-region.model';
import { GroupUser } from '../_models/group-user.model';
import { GroupUserResponsible } from '../_models/group-user-responsible.model';
import { JsonService } from '../_services/json.service';
import { ConfirmationDialogService } from '../_services/confirmation-dialog.service';
import { DialogService } from '../dialog/dialog.service';
import { LoggerService } from '../_services/logger.service';
import { LoadingService } from '../_services/loading.service';
import { GroupCountryRegionUser } from '../_models/group-country-region-user.model';
import { newGuid } from '../_helpers/guid.generator';
import { GlobalService } from '../_services/global.service';


@Component({
  selector: 'app-group-detail',
  templateUrl: './group-detail.component.html',
  styleUrls: ['./group-detail.component.scss']
})
export class GroupDetailComponent implements OnInit {
  // Public properties
  public dadosGrupo: Group = { id: '', name: '', groupCountryRegions: [] , groupUserResponsible: [], userMembers: 0 };
  public groupCountryRegions: any[] = [];
  public groupUserResponsible: any[] = [];
  public listaPaises: Array<any> = [];
  public listaRegiao: Array<any> = [];
  public allRegioes: Array<any> = [];
  public newAssociation: Partial<GroupCountryRegion> = { countryId: '', regionId: '', weight: 0 };
  public newResponsible: Partial<GroupUserResponsible> = { idGroup: '', idUser: '' };
  public nomeGrupo: string = '';
  public uid: string = '';
  public usersList: Array<GroupUser> = [];
  public showAddGroup: boolean = false;
  public usuarioSelecionado: string = '';
  public usuarioRespSelecionado: string = '';
  isModalOpen = false;
  isModal1Open = false;
  isModalResponsavelOpen = false;
  doShortcutActive = true;
  isInclusao = false;

  public selectedGroupCountryRegion: GroupCountryRegion | null = null;
  public availableUsers: GroupUser[] = [];
  public selectedUserId: string = '';
  public selectedUserRespId: string = '';
  public listaShowItens: any[] = [];
  public listaShowItensUsers: any[] = [];
  public listaShowItensUsersResp : any[] = [];
  public currentPage: number = 1;
  public pageSize: number = 15;
  public totalItems: number = 0;
  public isAdminGrupoUser: boolean = false;
  public isAdminGlobalGrupoUser: boolean = false;
  public isAdminUser: boolean = false;
  public currentUser: string = '';
  private allGroupsCache: Group[] | null = null;
  private staticDataLoaded = false;
  private postLoadAction: (() => void) | null = null;

  constructor(
    private jsonService: JsonService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private confirmationDialog: ConfirmationDialogService,
    private dialogService: DialogService,
    private logger: LoggerService,
    private translate: TranslateService,
    private loadingService: LoadingService,
    private globalService: GlobalService,
  ) { }

  ngOnInit(): void {
    this.currentUser = sessionStorage.getItem('username'); 
    this.isInclusao = this.route.snapshot.paramMap.get('action') === 'incluir';
    this.uid = this.route.snapshot.paramMap.get('uid') || '';

    if(this.isInclusao){
      this.showAddGroup = false
    }
    else {
      this.showAddGroup = true
    }

    this.isAdminGrupoUser = this.globalService.isAdminGrupo();
    this.isAdminGlobalGrupoUser = this.globalService.isAdminGlobalGrupo();
    this.isAdminUser = this.globalService.isAdmin();

    this.loadInitialDataAndGroup();
  }

  loadInitialDataAndGroup(): void {
    this.loadingService.show();

    if (this.staticDataLoaded) {
      this.loadGroupData();
      return;
    }

    forkJoin({
      countries: this.jsonService.listCountry(),
      regions:   this.jsonService.getRegiao(),
      users:     this.jsonService.listGroupUsers(),
      allGroups: this.jsonService.getGroupListWithUserResp(this.currentUser)
    }).subscribe(({ countries, regions, users, allGroups }) => {
      this.listaPaises      = countries.sort((a, b) => a.name.localeCompare(b.name));
      this.allRegioes       = regions;
      this.usersList        = users.sort((a, b) => a.email.localeCompare(b.email));
      this.allGroupsCache   = allGroups;
      this.staticDataLoaded = true;
      this.loadGroupData();
    },
    erro => {
      this.logger.error('Erro ao carregar dados iniciais', erro);
      this.loadingService.hide();
    });
  }

  private loadGroupData(): void {
    const id = this.uid || this.dadosGrupo?.id;
    if (!id) {
      this.loadingService.hide();
      return;
    }

    this.jsonService.getGroupDetail(id).subscribe(
      (groupData: any) => {
        this.dadosGrupo = groupData;
        this.nomeGrupo  = groupData.name;

        if (groupData.groupCountryRegions) {
          this.groupCountryRegions = groupData.groupCountryRegions.map(gcr => {
            const countryRegions = this.allRegioes
              .filter(regiao => regiao.countryId === gcr.countryId)
              .sort((a, b) => a.name.localeCompare(b.name));
            return {
              ...gcr,
              countryName: this.getCountryName(gcr.countryId),
              regionName:  this.getRegionName(gcr.regionId),
              regions: countryRegions,
              groupCountryRegionUsers: gcr.groupCountryRegionUsers || [],
              userMembers: gcr.groupCountryRegionUsers ? gcr.groupCountryRegionUsers.length : 0
            };
          }).sort((a, b) =>
            this.getCountryName(a.countryId).localeCompare(this.getCountryName(b.countryId)) ||
            (this.getRegionName(a.regionId) || '').localeCompare(this.getRegionName(b.regionId) || '')
          );
        }

        if (groupData.groupUserResponsible) {
          this.groupUserResponsible = groupData.groupUserResponsible.map(gur => ({
            idGroup:   this.dadosGrupo.id,
            idUser:    gur.userId,
            userEmail: this.usersList.find(u => u.id === gur.userId)?.email || '',
          })).sort((a, b) => a.userEmail.localeCompare(b.userEmail));
        }

        this.newAssociation = { countryId: '', regionId: '', weight: 0 };
        this.listaRegiao = [];
        this.loadingService.hide();

        if (this.postLoadAction) {
          const action = this.postLoadAction;
          this.postLoadAction = null;
          action();
        }
      },
      erro => {
        this.logger.error('Erro ao carregar detalhes do grupo', erro);
        this.loadingService.hide();
      }
    );
  }

  carregaFiltroRegioes(idPais: any): void {
    const countryId = idPais.target ? idPais.target.value : idPais;
    if (!countryId) {
      this.listaRegiao = [];
      return;
    }
    this.listaRegiao = this.allRegioes.filter(regiao => regiao.countryId == countryId).sort((a,b) => a.name.localeCompare(b.name));
  }

  carregaRegioesDaLinha(gcr: any): void {
    const countryId = gcr.countryId;
    gcr.regionId = null;
    if (!countryId) {
      gcr.regions = [];
      return;
    }
    gcr.regions = this.allRegioes.filter(regiao => regiao.countryId == countryId).sort((a,b) => a.name.localeCompare(b.name));
  }

adicionarAssociacao(): void {
  if (!this.dadosGrupo.name) {
    const title = this.translate.instant('Atenção') + '!';
    const message = this.translate.instant('Informe o nome para o grupo!');
    this.dialogService.abrirDialog('aviso', title, message);
    return;
  }

  if (!this.newAssociation.countryId || !this.newAssociation.weight) {
    const title = this.translate.instant('Atenção') + '!';
    const message = this.translate.instant('Preencha todos os campos obrigatórios!');
    this.dialogService.abrirDialog('aviso', title, message);
    return;
  }

  // opcional: se você usa loading aqui
  this.loadingService.show();

  const newAssoc: GroupCountryRegion = {
    countryId: this.newAssociation.countryId,
    regionId: this.newAssociation.regionId || null,
    countryName: this.getCountryName(this.newAssociation.countryId),
    regionName: this.getRegionName(this.newAssociation.regionId || ''),
    weight: this.newAssociation.weight,
  };

  const exists = this.dadosGrupo.groupCountryRegions.some(
    gcr => gcr.countryId === newAssoc.countryId && gcr.regionId === newAssoc.regionId
  );

  if (exists) {
    const title = this.translate.instant('Atenção') + '!';
    const message = this.translate.instant('Esta associação de país/região já existe para este grupo.');
    this.dialogService.abrirDialog('aviso', title, message);
    
    return;
  }

  this.dadosGrupo.groupCountryRegions.push(newAssoc);

  this.validaPesos$().pipe(
    take(1),
    switchMap((ok) => {
      if (!ok) {
        this.dadosGrupo.groupCountryRegions.pop();
        this.newAssociation = { countryId: '', regionId: '', weight: 0 };
        return EMPTY;
      }

      if (!this.isInclusao) {
        this.saveGroupChanges('Associação adicionada com sucesso!');
      } else {
        this.groupCountryRegions = this.dadosGrupo.groupCountryRegions.map(gcr => {
          const countryRegions = this.allRegioes
            .filter(regiao => regiao.countryId === gcr.countryId)
            .sort((a, b) => a.name.localeCompare(b.name));

          return {
            ...gcr,
            countryName: this.getCountryName(gcr.countryId),
            regionName: this.getRegionName(gcr.regionId),
            regions: countryRegions,
          };
        });

        this.saveGroupChanges('Associação adicionada com sucesso!');

      }

      this.newAssociation = { countryId: '', regionId: '', weight: 0 };

      // como já fizemos tudo aqui dentro, só finaliza o observable
      return EMPTY;
    }),
    finalize(() => this.loadingService.hide())
  ).subscribe();
}
  removeUserResponsible(gurToRemove: GroupUserResponsible): void {

    const formData = new FormData();
    formData.append('userId', gurToRemove.idUser);
    formData.append('groupId', gurToRemove.idGroup);  

    this.loadingService.show();
    this.jsonService.removeUserResponsible(formData).subscribe(
      () => {
        this.loadingService.hide();
        let title = this.translate.instant('Éxito') + '!';
//        let message = this.translate.instant('Usuário removido com sucesso!');
  //      this.dialogService.abrirDialog('aviso', title, message);
        this.loadInitialDataAndGroup(); // Reload all data
      },
      erro => {
        this.loadingService.hide();
        this.logger.error('Erro ao remover usuário', erro);
        let title = this.translate.instant('Atenção') + '!';
        let message = this.translate.instant('Erro ao remover usuário responsável.');
        this.dialogService.abrirDialog('aviso', title, message);
      })  
  }
  
  addUserResponsible(): void {

    if (!this.newResponsible.idUser) {
      let title = this.translate.instant('Atenção') + '!';
      let message = this.translate.instant('Selecione um usuário para adicionar como responsável.');
      this.dialogService.abrirDialog('aviso', title, message);
     
      return;
    }

    const newResp: GroupUserResponsible = {
      idGroup: this.dadosGrupo.id,
      idUser: this.newResponsible.idUser
    };

    const exists = this.dadosGrupo.groupUserResponsible.some(
      gcr => gcr.idGroup === newResp.idGroup && gcr.idUser === newResp.idUser
    );

    if (exists) {
      let title = this.translate.instant('Atenção') + '!';
      let message = this.translate.instant('Esta associação de usuário responsável já existe para este grupo.');
      this.dialogService.abrirDialog('aviso', title, message);
      return;
    }

    const formData = new FormData();
    formData.append('userId', newResp.idUser);
    formData.append('groupId', newResp.idGroup);
  
    this.loadingService.show();
    this.jsonService.addUserResponsible(formData).subscribe(
      () => {
        this.loadingService.hide();
        let title = this.translate.instant('Éxito') + '!';
//        let message = this.translate.instant('Usuário responsável adicionado com sucesso!');
//        this.dialogService.abrirDialog('aviso', title, message);
        this.loadInitialDataAndGroup(); // Reload all data
      },
      erro => {
        this.loadingService.hide();
        this.logger.error('Erro ao associar usuário', erro);
        let title = this.translate.instant('Atenção') + '!';
        let message = this.translate.instant('Erro ao associar usuário responsável usuário.');
        this.dialogService.abrirDialog('aviso', title, message);
      }
    );


    this.newResponsible = { idGroup: '', idUser: '' };  
  }

  async removeGroupCountryRegion(gcrToRemove: GroupCountryRegion): Promise<void> {
    let title = this.translate.instant('Atenção') + '!';
    let texto = this.translate.instant('Tem certeza de que deseja remover esta associação de país/região do grupo?');
    const confirmed = await this.confirmationDialog.confirm(title, texto);

    if (confirmed) {
      this.dadosGrupo.groupCountryRegions = this.dadosGrupo.groupCountryRegions.filter(
        gcr => !(gcr.countryId === gcrToRemove.countryId && gcr.regionId === gcrToRemove.regionId)
      );
      this.saveGroupChanges('Associação removida com sucesso!');
    }
  }

  gravarGrupo() {

      if (!this.nomeGrupo) {
        let title = this.translate.instant('Atenção') + '!';
        let message = this.translate.instant('Preencha todos os campos obrigatórios!');
        this.dialogService.abrirDialog('aviso', title, message);

        return;
      }

    if (this.isInclusao) {
      this.createNewGroupName();
      this.showAddGroup = true;
    } else {
      this.updateGroupName();
    }
  }


  createNewGroupName(): void {
      this.dadosGrupo.id = newGuid();
      this.dadosGrupo.name = this.nomeGrupo;
      this.dadosGrupo.userMembers = 0;
      this.dadosGrupo.groupUserResponsible = [];
      this.saveNewGroup();
  }


validaPesos$(): Observable<boolean> {
  const source$ = this.allGroupsCache
    ? of(this.allGroupsCache)
    : this.jsonService.getGroupListWithUserResp(this.currentUser).pipe(
        tap(groups => { this.allGroupsCache = groups; })
      );

  return source$.pipe(
    map((allGroups: Group[]) => {
      const weightSums = new Map<string, number>();

      const currentIds = new Set(
        (this.dadosGrupo.groupCountryRegions || []).map(x => x.idGrupoRegion)
      );

      for (const group of allGroups || []) {
        // se quiser excluir o próprio grupo por ID:
        // if (group.id === this.dadosGrupo.id) continue;

        for (const gcr of group.groupCountryRegions || []) {
          if (currentIds.has(gcr.idGrupoRegion)) continue;

          const key = `${gcr.countryId}-${gcr.regionId ?? 'null'}`;
          const currentSum = weightSums.get(key) ?? 0;
          weightSums.set(key, currentSum + Number(gcr.weight ?? 0));
        }
      }

      for (const gcr of this.dadosGrupo.groupCountryRegions || []) {
        const key = `${gcr.countryId}-${gcr.regionId ?? 'null'}`;
        const sumFromOtherGroups = weightSums.get(key) ?? 0;

        if (sumFromOtherGroups + Number(gcr.weight ?? 0) > 100) {
          const countryName = this.getCountryName(gcr.countryId);
          const regionName = gcr.regionId ? this.getRegionName(gcr.regionId) : 'sem região';

          const title = this.translate.instant('Atenção') + '!';
          const message =
            `A soma dos pesos para ${countryName} / ${regionName} excede 100. ` +
            `Soma nos outros grupos: ${sumFromOtherGroups}%.`;

          this.dialogService.abrirDialog('aviso', title, message);
          return false;
        }
      }

      return true;
    }),
    catchError((erro) => {
      this.logger.error('Erro ao carregar lista de grupos para validação', erro);
      this.dialogService.abrirDialog('erro', 'Erro', 'Falha ao validar pesos.');
      return of(false);
    })
  );
}

  saveNewGroup() {

        this.loadingService.show();
        const payload = {
            id: this.dadosGrupo.id,
            name: this.nomeGrupo,
            userMembers: 0,
            groupCountryRegions: this.dadosGrupo.groupCountryRegions,
            groupUserResponsibles: this.dadosGrupo.groupUserResponsible || []
        };

        this.jsonService.createGroup(payload).subscribe(
            () => {
                const newId = this.dadosGrupo.id;
                this.uid = newId;
                this.isInclusao = false;
                this.showAddGroup = true;
                this.allGroupsCache = null;
                this.loadingService.hide();
                this.loadInitialDataAndGroup();
                this.router.navigate(['/grupo-detalhe', newId, 'new', 'editar']);
            },
            erro => {
                this.logger.error('Erro ao criar grupo', erro);
                this.loadingService.hide();
            }
        );
  } 

  updateGroupName(): void {

    this.dadosGrupo.name = this.nomeGrupo;
    this.saveGroupChanges('Nome do grupo atualizado com sucesso!');
  }
  
  saveAssociations(): void {
    const cleanedGCRs = this.groupCountryRegions.map(gcr => {
      const { regions, ...rest } = gcr;
      return rest;
    });

    this.dadosGrupo.groupCountryRegions = cleanedGCRs;

    this.loadingService.show();

      this.validaPesos$().pipe(
      take(1),
      switchMap((ok) => {
        if (!ok) return EMPTY;

        this.saveGroupChanges('Associações salvas com sucesso!');

        return EMPTY;
      }),
      finalize(() => this.loadingService.hide())
    ).subscribe();
    
  }

  adicionarUsuarioaGrupo(idGrupoRegion: string) {
    const gcr = this.groupCountryRegions.find(g => g.idGrupoRegion === idGrupoRegion);
    if (gcr) {
      this.selectedGroupCountryRegion = gcr;
      const usersInGcr = gcr.groupCountryRegionUsers || [];
      const usersInGcrIds = usersInGcr.map(u => u.iduser);
      this.listaShowItensUsers = usersInGcr.map(u => {
        const user = this.usersList.find(ul => ul.id === u.idUser);
        return {
          ...u,
          email: user ? user.email : ''
        };
      });

      this.totalItems = this.listaShowItensUsers.length;
      this.availableUsers = this.usersList.filter(u => !usersInGcrIds.includes(u.id));
      this.selectedUserId = '';
      this.isModal1Open = true;
    }
  }

  async removeOfGrupo(userToRemove: any) {
    if (!this.selectedGroupCountryRegion) return;

    const gcrId = this.selectedGroupCountryRegion.idGrupoRegion!;

    let hasPending = false;
    try {
      this.loadingService.show();
      const result = await firstValueFrom(this.jsonService.checkGcruHasPrescriptionsPending(gcrId));
      hasPending = result?.hasPending ?? false;
    } catch {
      // ignore check error, proceed with simple confirmation
    } finally {
      this.loadingService.hide();
    }

    const title = this.translate.instant('Atenção') + '!';
    const texto = hasPending
      ? this.translate.instant('Existem prescrições pendentes (status P) vinculadas a esta região. Confirma a remoção do usuário?')
      : this.translate.instant('Tem certeza de que deseja remover este usuário do grupo?');
    const confirmed = await this.confirmationDialog.confirm(title, texto);
    if (!confirmed) return;

    const user = this.currentUser ?? '';
    this.loadingService.show();
    this.jsonService.toggleActiveGroupCountryRegionUser({
      groupCountryRegionId: gcrId,
      userId: userToRemove.idUser,
      isActive: false,
      updatedBy: user
    }).subscribe(
      () => {
        this.loadingService.hide();
        this.postLoadAction = () => this.adicionarUsuarioaGrupo(gcrId);
        this.loadInitialDataAndGroup();
      },
      erro => {
        this.loadingService.hide();
        this.logger.error('Erro ao remover usuário', erro);
        const errTitle = this.translate.instant('Atenção') + '!';
        const errMsg = this.translate.instant('Erro ao remover usuário.');
        this.dialogService.abrirDialog('aviso', errTitle, errMsg);
      }
    );
  }

  async toggleActiveGroup(isActive: boolean) {
    const title = this.translate.instant('Atenção') + '!';
    const msg = isActive
      ? this.translate.instant('Deseja reativar este grupo?')
      : this.translate.instant('Tem certeza de que deseja desativar este grupo?');
    const confirmed = await this.confirmationDialog.confirm(title, msg);
    if (!confirmed) return;

    const user = this.currentUser ?? '';
    this.loadingService.show();
    this.jsonService.toggleActiveGroup({ id: this.dadosGrupo.id, isActive, updatedBy: user }).subscribe(
      () => {
        this.loadingService.hide();
        this.loadInitialDataAndGroup();
      },
      erro => {
        this.loadingService.hide();
        this.logger.error('Erro ao alterar status do grupo', erro);
        const errTitle = this.translate.instant('Atenção') + '!';
        const errMsg = erro?.error?.message
          || this.translate.instant('Não é possível desativar. Existem prescrições ativas vinculadas ao grupo.');
        this.dialogService.abrirDialog('aviso', errTitle, errMsg);
      }
    );
  }

  associateUser() {
    if (!this.selectedUserId || !this.selectedGroupCountryRegion) {
      return;
    }

    const gcrId = this.selectedGroupCountryRegion.idGrupoRegion!;
    const formData = new FormData();
    formData.append('userId', this.selectedUserId);
    formData.append('groupCountryRegionId', gcrId);

    this.loadingService.show();
    this.jsonService.createAssociation(formData).subscribe(
      () => {
        this.loadingService.hide();
        this.postLoadAction = () => this.adicionarUsuarioaGrupo(gcrId);
        this.loadInitialDataAndGroup();
      },
      erro => {
        this.loadingService.hide();
        this.logger.error('Erro ao associar usuário', erro);
        let title = this.translate.instant('Atenção') + '!';
        let message = this.translate.instant('Erro ao associar usuário.');
        this.dialogService.abrirDialog('aviso', title, message);
      }
    );
  }

  back(): void {
    this.router.navigate(['/grupo-lista']);
  }

  private saveGroupChanges(successMessage: string): void {
    this.loadingService.show();

        const groupDataToSend = JSON.parse(JSON.stringify(this.dadosGrupo));
    
        this.jsonService.putGroupUpdateUrl(groupDataToSend).subscribe(
            () => {
                this.allGroupsCache = null;
                let title = this.translate.instant('Éxito') + '!';
                this.dialogService.abrirDialog('aviso', title, this.translate.instant(successMessage));
                this.loadInitialDataAndGroup();
            },
            erro => {
                this.logger.error('Erro ao atualizar grupo', erro);
                this.loadingService.hide();
                const title = this.translate.instant('Atenção') + '!';
                const message = this.translate.instant('Erro ao salvar alterações. Tente novamente.');
                this.dialogService.abrirDialog('erro', title, message);
            }
        );


  }
  
  private getRegionName(id: string): string {
    if (!id) return 'Todas';
    const region = this.allRegioes.find(regiao => regiao.id == id);
    return region ? region.name : '';
  }

  private getCountryName(id: string): string {
    if (!id) return '';
    const country = this.listaPaises.find(pais => pais.id == id);
    return country ? country.name : '';
  }

  // ------------ Modais -----------------
  openModal() { 
    this.isModalOpen = true; 
  }
  closeModal(shortcut = false) { 
    this.isModalOpen = false; 
    if (shortcut) this.doShortcutActive = true; 
  }
  closeModal1() { 
    this.isModal1Open = false; 
    this.selectedGroupCountryRegion = null;
    this.listaShowItens = [];
    this.availableUsers = [];
    this.selectedUserId = '';
  }

  openModalResponsavel() {
    this.isModalResponsavelOpen = true;
  }

  closeModalResponsavel() {
    this.isModalResponsavelOpen = false;
  }
}